import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1' ? 'http://127.0.0.1:8001/api' : '/api');

// Site config interface
export interface SiteConfig {
  site_name: string;
  site_tagline: string;
  site_description: string;
  company_name: string;
  company_address: string;
  company_phone: string;
  company_email: string;
  company_website: string;
  business_hours: string;
  about_text: string;
  welcome_message: string;
  facebook_url: string;
  instagram_url: string;
  twitter_url: string;
  linkedin_url: string;
  meta_title: string;
  meta_description: string;
  meta_keywords: string;
  og_title: string;
  og_description: string;
  og_image_url: string;
  canonical_url: string;
  google_analytics_id: string;
  gtm_container_id: string;
  robots_txt: string;
  footer_text: string;
  footer_copyright: string;
  footer_tagline: string;
  maintenance_mode: boolean;
  maintenance_message: string;
  custom_settings: Record<string, any>;
}

class SiteConfigService {
  private config: SiteConfig | null = null;
  private configPromise: Promise<SiteConfig> | null = null;

  // Get public site config (no auth required)
  async getPublicConfig(): Promise<SiteConfig> {
    try {
      const response = await axios.get(`${API_BASE_URL}/site/site/`);
      return response.data;
    } catch (error) {
      console.error('Failed to fetch public site config:', error);
      // Return default config if API fails
      return this.getDefaultConfig();
    }
  }

  // Get authenticated site config (with auth token)
  async getAuthenticatedConfig(): Promise<SiteConfig> {
    const token = localStorage.getItem('token');
    if (!token) {
      return this.getPublicConfig();
    }

    try {
      const response = await axios.get(`${API_BASE_URL}/site/site-authenticated/`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      return response.data;
    } catch (error) {
      console.error('Failed to fetch authenticated site config:', error);
      // Fallback to public config
      return this.getPublicConfig();
    }
  }

  // Get cached config or fetch new one
  async getConfig(useAuth: boolean = false, forceRefresh: boolean = false): Promise<SiteConfig> {
    if (this.config && !forceRefresh) {
      return this.config;
    }

    if (this.configPromise && !forceRefresh) {
      return this.configPromise;
    }

    this.configPromise = useAuth ? 
      this.getAuthenticatedConfig() : 
      this.getPublicConfig();

    this.config = await this.configPromise;
    this.configPromise = null;

    // Expose GA ID globally so CookieConsent can load analytics
    if (this.config.google_analytics_id) {
      (window as any).__GA_ID__ = this.config.google_analytics_id;
    }
    // Expose GTM ID globally so CookieConsent can load Tag Manager
    if (this.config.gtm_container_id) {
      (window as any).__GTM_ID__ = this.config.gtm_container_id;
    }

    return this.config;
  }

  // Clear cached config (useful when settings are updated)
  clearCache(): void {
    this.config = null;
    this.configPromise = null;
  }

  // Get specific config value
  async getValue(key: keyof SiteConfig, useAuth: boolean = false, forceRefresh: boolean = false): Promise<string | boolean | Record<string, any>> {
    const config = await this.getConfig(useAuth, forceRefresh);
    return config[key];
  }

  // Get default configuration
  private getDefaultConfig(): SiteConfig {
    return {
      site_name: 'Wholesale Bakery',
      site_tagline: '',
      site_description: '',
      company_name: 'Wholesale Bakery',
      company_address: '',
      company_phone: '',
      company_email: '',
      company_website: '',
      business_hours: '',
      about_text: '',
      welcome_message: 'Welcome to our wholesale bakery platform!',
      facebook_url: '',
      instagram_url: '',
      twitter_url: '',
      linkedin_url: '',
      meta_title: '',
      meta_description: '',
      meta_keywords: '',
      og_title: '',
      og_description: '',
      og_image_url: '',
      canonical_url: '',
      google_analytics_id: '',
      gtm_container_id: '',
      robots_txt: '',
      maintenance_mode: false,
      maintenance_message: '',
      custom_settings: {},
      footer_text: 'Wholesale Management System',
      footer_copyright: '',
      footer_tagline: 'Wholesale bakery management platform.\nBuilt with care in Texas.'
    };
  }
}

export const siteConfigService = new SiteConfigService();
export default siteConfigService;